import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Plus, Trash2, X, Image as ImageIcon, UploadCloud, Search, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function OfferBannerManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [status, setStatus] = useState("Active");
    const [isUploading, setIsUploading] = useState(false);

    // Lightbox / Image view modal
    const [viewingImage, setViewingImage] = useState(null);

    const fetchBanners = () => {
        setLoading(true);
        fetch(`${API_BASE_URL}/api/offer-banners/all`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch offer banners");
                return res.json();
            })
            .then((resJson) => {
                if (resJson && resJson.status && resJson.data) {
                    setBanners(resJson.data);
                } else {
                    setBanners([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching offer banners:", err);
                setBanners([]);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                Swal.fire("Invalid File", "Please select an image file (PNG, JPG, WEBP).", "warning");
                return;
            }
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (!file.type.startsWith("image/")) {
                Swal.fire("Invalid File", "Please drop an image file (PNG, JPG, WEBP).", "warning");
                return;
            }
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setSelectedFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
        setStatus("Active");
        setIsModalOpen(false);
    };

    const handleAddBanner = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            Swal.fire("Image Required", "Please select or drop an offer banner image to upload.", "warning");
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("banner_image", selectedFile);
            formData.append("status", status);

            const res = await fetch(`${API_BASE_URL}/api/offer-banners/add`, {
                method: "POST",
                body: formData
            });

            const resJson = await res.json();

            if (res.ok && resJson.status) {
                Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: "Offer banner uploaded successfully.",
                    timer: 2000,
                    showConfirmButton: false
                });
                resetForm();
                fetchBanners();
            } else {
                throw new Error(resJson.message || "Failed to upload banner");
            }
        } catch (err) {
            console.error("Upload error:", err);
            Swal.fire("Upload Failed", err.message || "An error occurred while uploading.", "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteBanner = (id) => {
        Swal.fire({
            title: "Delete Offer Banner?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${API_BASE_URL}/api/offer-banners/${id}`, {
                        method: "DELETE"
                    });
                    const resJson = await res.json();
                    if (res.ok && resJson.status) {
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: "Offer banner deleted successfully.",
                            timer: 1500,
                            showConfirmButton: false
                        });
                        fetchBanners();
                    } else {
                        throw new Error(resJson.message || "Failed to delete banner");
                    }
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error", err.message || "Failed to delete banner", "error");
                }
            }
        });
    };

    const filteredBanners = banners.filter((b) => {
        const matchesSearch = !searchTerm || String(b.id).includes(searchTerm) || (b.status || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "All" || (b.status || "Active").toLowerCase() === statusFilter.toLowerCase();
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
                <Header
                    onMenuClick={() => setIsSidebarOpen(true)}
                    title="Offer Banners"
                    subtitle="Upload and manage promotional offer banners."
                />

                <div className="p-4 sm:p-8 flex-1 w-full max-w-7xl mx-auto">
                    {/* Header Action Bar */}
                    <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Banners:</span>
                                <span className="text-base font-bold text-blue-600 dark:text-blue-400">{banners.length}</span>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {/* Search */}
                            <div className="relative">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search by ID or Status..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="bg-white dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder-slate-400"
                                />
                            </div>

                            {/* Status filter */}
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-white dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-500 transition-all cursor-pointer"
                            >
                                <option value="All">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>

                            {/* Add Banner Button */}
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg transition-all active:scale-95"
                            >
                                <Plus size={18} />
                                <span>Add Offer Banner</span>
                            </button>
                        </div>
                    </div>

                    {/* Main Grid View */}
                    {loading ? (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center">
                            <div className="flex flex-col items-center justify-center gap-3">
                                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-slate-500 dark:text-slate-400 font-medium">Loading offer banners...</p>
                            </div>
                        </div>
                    ) : filteredBanners.length === 0 ? (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center shadow-sm">
                            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <ImageIcon size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">No Offer Banners Found</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-6">
                                {searchTerm || statusFilter !== "All"
                                    ? "No banners match your current search or status filter."
                                    : "You haven't uploaded any offer banners yet. Click the button below to upload your first banner."}
                            </p>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all"
                            >
                                <Plus size={18} />
                                <span>Add First Offer Banner</span>
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredBanners.map((banner) => (
                                <div
                                    key={banner.id}
                                    className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col group"
                                >
                                    {/* Banner Image Container */}
                                    <div className="relative aspect-[16/9] bg-slate-100 dark:bg-slate-900 overflow-hidden cursor-pointer">
                                        <img
                                            src={banner.banner_image}
                                            alt={`Offer Banner #${banner.id}`}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            onClick={() => setViewingImage(banner.banner_image)}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://placehold.co/600x338?text=Banner+Image";
                                            }}
                                        />

                                        {/* Overlay view icon on hover */}
                                        <div
                                            onClick={() => setViewingImage(banner.banner_image)}
                                            className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-medium text-sm backdrop-blur-[2px]"
                                        >
                                            <Eye size={18} />
                                            <span>View Full Image</span>
                                        </div>

                                        {/* Status Badge */}
                                        <div className="absolute top-3 left-3">
                                            <span
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-md shadow-sm ${banner.status === "Active"
                                                        ? "bg-emerald-500/90 text-white"
                                                        : "bg-slate-600/90 text-white"
                                                    }`}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full ${banner.status === "Active" ? "bg-white animate-pulse" : "bg-slate-300"}`}></span>
                                                {banner.status || "Active"}
                                            </span>
                                        </div>

                                        {/* ID Badge */}
                                        <div className="absolute top-3 right-3">
                                            <span className="bg-slate-900/70 text-white text-xs font-bold px-2 py-1 rounded-md backdrop-blur-md">
                                                #{banner.id}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Banner Details & Action Footer */}
                                    <div className="p-4 flex items-center justify-between border-t border-slate-100 dark:border-slate-700/60 mt-auto">
                                        <div>
                                            <span className="text-xs text-slate-400 block">Uploaded On</span>
                                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                {banner.created_at
                                                    ? new Date(banner.created_at).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric"
                                                    })
                                                    : "N/A"}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handleDeleteBanner(banner.id)}
                                            title="Delete Offer Banner"
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Banner Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-scaleUp">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Add New Offer Banner</h3>
                                <p className="text-xs text-slate-400 mt-0.5">Upload a banner image to showcase promotional offers</p>
                            </div>
                            <button
                                onClick={resetForm}
                                disabled={isUploading}
                                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleAddBanner} className="p-6 space-y-5">
                            {/* Image Upload Area */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-2">
                                    Offer Banner Image <span className="text-red-500">*</span>
                                </label>

                                {previewUrl ? (
                                    <div className="relative rounded-xl overflow-hidden border-2 border-blue-500/50 bg-slate-50 dark:bg-slate-900 aspect-[16/9] flex items-center justify-center group">
                                        <img
                                            src={previewUrl}
                                            alt="Banner Preview"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                            <label className="cursor-pointer bg-white text-slate-800 text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm hover:bg-slate-100 transition-all">
                                                Change Image
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="hidden"
                                                    disabled={isUploading}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedFile(null);
                                                    if (previewUrl) URL.revokeObjectURL(previewUrl);
                                                    setPreviewUrl("");
                                                }}
                                                disabled={isUploading}
                                                className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm hover:bg-red-600 transition-all"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={handleDrop}
                                        className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 rounded-xl p-8 cursor-pointer bg-slate-50/50 dark:bg-slate-900/50 hover:bg-blue-50/30 transition-all group"
                                    >
                                        <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <UploadCloud size={28} />
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                            Click to upload or drag & drop
                                        </span>
                                        <span className="text-xs text-slate-400 mt-1">
                                            Supports JPG, PNG, WEBP (Recommended: 16:9 or banner ratio)
                                        </span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            disabled={isUploading}
                                        />
                                    </label>
                                )}
                            </div>

                            {/* Status Selector */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-2">
                                    Status
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setStatus("Active")}
                                        className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border text-sm font-semibold transition-all ${status === "Active"
                                                ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 text-emerald-700 dark:text-emerald-400 shadow-sm"
                                                : "border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50"
                                            }`}
                                    >
                                        <CheckCircle2 size={16} />
                                        <span>Active</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setStatus("Inactive")}
                                        className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border text-sm font-semibold transition-all ${status === "Inactive"
                                                ? "bg-slate-100 dark:bg-slate-700 border-slate-400 text-slate-700 dark:text-slate-200 shadow-sm"
                                                : "border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50"
                                            }`}
                                    >
                                        <AlertCircle size={16} />
                                        <span>Inactive</span>
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    disabled={isUploading}
                                    className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-all"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isUploading || !selectedFile}
                                    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-all ${isUploading || !selectedFile
                                            ? "bg-slate-400 cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20 active:scale-95"
                                        }`}
                                >
                                    {isUploading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Uploading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <UploadCloud size={16} />
                                            <span>Upload Banner</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Image Lightbox Modal */}
            {viewingImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-fadeIn"
                    onClick={() => setViewingImage(null)}
                >
                    <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center">
                        <button
                            onClick={() => setViewingImage(null)}
                            className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 rounded-full transition-all"
                        >
                            <X size={28} />
                        </button>
                        <img
                            src={viewingImage}
                            alt="Full Banner"
                            className="max-h-[85vh] w-auto max-w-full rounded-2xl shadow-2xl object-contain border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
